import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import {
  EntityConfigurationEntityType,
  EntityConfigurationType,
  useUpdateConfiguration,
} from "@/api/rest/services/configurationService";
import { findUserByEmail } from "@/api/rest/services/userService";
import { ApiBanner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import Field from "@/components/common/Field";
import Input from "@/components/common/Input";
import TextArea from "@/components/common/TextArea";
import Checkbox from "@/components/common/Checkbox";

export interface EmailForwardingRule {
  userId?: number;
  email: string;
  name: string;
  description: string;
  tempId: string; // For React key
}

export interface EmailForwardingConfiguration {
  enabled: boolean;
  rules: EmailForwardingRule[];
}

interface EmailForwardingSettingsProps {
  initialConfiguration?: EmailForwardingConfiguration;
  buildingId: number;
}

export function EmailForwardingSettings({
  initialConfiguration = { enabled: false, rules: [] },
  buildingId,
}: EmailForwardingSettingsProps) {
  const { t } = useTranslation();
  const { mutate: updateConfiguration, isPending: isSaving } = useUpdateConfiguration();
  const nextIdRef = useRef(0);
  const [allowForwarding, setAllowForwarding] = useState(initialConfiguration.enabled);
  const [forwardingRules, setForwardingRules] = useState<EmailForwardingRule[]>(
    initialConfiguration.rules.map((rule, index) => ({
      ...rule,
      tempId: `rule-${index}`,
    })),
  );
  const [initialForwardingRules, setInitialForwardingRules] = useState<EmailForwardingRule[]>(
    initialConfiguration.rules.map((rule, index) => ({
      ...rule,
      tempId: `rule-${index}`,
    })),
  );
  const [saveResult, setSaveResult] = useState<ApiResponse<unknown> | null>(null);
  const [emailErrors, setEmailErrors] = useState<Record<string, string | null>>({});
  const [emailLoading, setEmailLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const rulesWithTempId = initialConfiguration.rules.map((rule, index) => ({
      ...rule,
      tempId: `rule-${index}`,
    }));
    setForwardingRules(rulesWithTempId);
    setInitialForwardingRules(rulesWithTempId);
    setAllowForwarding(initialConfiguration.enabled);
    nextIdRef.current = initialConfiguration.rules.length;
  }, [initialConfiguration]);

  const validateEmail = useCallback(
    async (tempId: string, email: string) => {
      if (!email.trim()) {
        setEmailErrors((prev) => ({ ...prev, [tempId]: null }));
        return;
      }

      setEmailLoading((prev) => ({ ...prev, [tempId]: true }));
      const response = await findUserByEmail(email, buildingId);
      setEmailLoading((prev) => ({ ...prev, [tempId]: false }));

      if (response.success && response.result) {
        setEmailErrors((prev) => ({ ...prev, [tempId]: null }));
        setForwardingRules((rules) =>
          rules.map((rule) => (rule.tempId === tempId ? { ...rule, userId: response.result.id } : rule)),
        );
      } else {
        setEmailErrors((prev) => ({
          ...prev,
          [tempId]: t("buildingSettings.questionsForwarding.emailInvalid"),
        }));
      }
    },
    [buildingId, t],
  );

  const addRule = () => {
    if (forwardingRules.length < 10) {
      const newRule: EmailForwardingRule = {
        email: "",
        name: "",
        description: "",
        tempId: `rule-${nextIdRef.current++}`,
      };
      setForwardingRules([...forwardingRules, newRule]);
      setSaveResult(null);
    }
  };

  const removeRule = (tempId: string) => {
    const updatedRules = forwardingRules.filter((rule) => rule.tempId !== tempId);
    setForwardingRules(updatedRules);
    setEmailErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[tempId];
      return newErrors;
    });
    if (updatedRules.length === 0) {
      setAllowForwarding(false);
      handleSave(false, { showBanner: false });
    }
    setSaveResult(null);
  };

  const updateRule = (tempId: string, field: keyof Omit<EmailForwardingRule, "tempId" | "userId">, value: string) => {
    setForwardingRules(forwardingRules.map((rule) => (rule.tempId === tempId ? { ...rule, [field]: value } : rule)));
    setSaveResult(null);
  };

  const hasEmailErrors = useMemo(() => Object.values(emailErrors).some((error) => error !== null), [emailErrors]);

  const isAnyRuleDirty = useMemo(() => {
    if (allowForwarding !== initialConfiguration.enabled) {
      return true;
    }
    if (forwardingRules.length !== initialForwardingRules.length) {
      return true;
    }
    return forwardingRules.some((currentRule) => {
      const initialRule = initialForwardingRules.find((r) => r.tempId === currentRule.tempId);
      if (!initialRule) {
        return true;
      }
      return (
        currentRule.email !== initialRule.email ||
        currentRule.name !== initialRule.name ||
        currentRule.description !== initialRule.description
      );
    });
  }, [allowForwarding, forwardingRules, initialConfiguration.enabled, initialForwardingRules]);

  const handleSave = (enabled?: boolean, options?: { showBanner: boolean }) => {
    if (options?.showBanner) {
      setSaveResult(null);
    }
    const rulesToSave = forwardingRules.filter((rule) => rule.userId);
    const configuration: EmailForwardingConfiguration = {
      enabled: enabled ?? allowForwarding,
      rules: rulesToSave,
    };

    updateConfiguration(
      {
        entityType: EntityConfigurationEntityType.BUILDING,
        buildingId: buildingId,
        configurationType: EntityConfigurationType.ESCALATION_QUESTION_EMAIL_FORWARDING_RULE,
        configurationValue: JSON.stringify(configuration),
      },
      {
        onSuccess: () => {
          const newInitialRules = forwardingRules.map((rule) => ({ ...rule }));
          setInitialForwardingRules(newInitialRules);
          if (options?.showBanner) {
            setSaveResult({ success: true, result: {} });
          }
        },
        onError: (error) => {
          if (options?.showBanner) {
            setSaveResult({
              success: false,
              message: error.message,
              result: undefined,
            });
          }
        },
      },
    );
  };

  return (
    <div>
      <Checkbox
        label={t("buildingSettings.questionsForwarding.allowForwarding")}
        checked={allowForwarding}
        onChange={(e) => {
          const isChecked = e.target.checked;
          setAllowForwarding(isChecked);
          if (isChecked && forwardingRules.length === 0) {
            addRule();
          }
          handleSave(isChecked, { showBanner: false });
        }}
      />

      {allowForwarding && (
        <div className="mt-4 pl-2">
          {forwardingRules.map((rule) => (
            <div key={rule.tempId} className="relative mb-4 p-3 border rounded-md bg-slate-50">
              <button
                onClick={() => removeRule(rule.tempId)}
                className="absolute top-1 right-1 p-1 text-red-600 hover:text-red-800"
                aria-label={t("buildingSettings.questionsForwarding.removeRule")}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                <Field hasError={Boolean(emailErrors[rule.tempId])} id={`email-${rule.tempId}`}>
                  <Field.Label>{t("buildingSettings.questionsForwarding.email")}</Field.Label>
                  <Input
                    type="text"
                    value={rule.email}
                    onChange={(e) => updateRule(rule.tempId, "email", e.target.value)}
                    onBlur={(e) => validateEmail(rule.tempId, e.target.value)}
                    placeholder={t("buildingSettings.questionsForwarding.emailPlaceholder")}
                  />
                  {emailLoading[rule.tempId] && <p className="mt-1 text-sm text-gray-500">{t("common.validating")}</p>}
                  <Field.Error>{emailErrors[rule.tempId]}</Field.Error>
                </Field>
                <Field id={`name-${rule.tempId}`}>
                  <Field.Label>{t("buildingSettings.questionsForwarding.name")}</Field.Label>
                  <Input
                    type="text"
                    value={rule.name}
                    onChange={(e) => updateRule(rule.tempId, "name", e.target.value)}
                    placeholder={t("buildingSettings.questionsForwarding.namePlaceholder")}
                  />
                </Field>
              </div>
              <Field id={`description-${rule.tempId}`}>
                <Field.Label>{t("buildingSettings.questionsForwarding.description")}</Field.Label>
                <TextArea
                  value={rule.description}
                  onChange={(e) => updateRule(rule.tempId, "description", e.target.value)}
                  rows={2}
                  placeholder={t("buildingSettings.questionsForwarding.descriptionPlaceholder")}
                />
              </Field>
            </div>
          ))}
          <div className="flex items-center justify-between mt-4 w-full gap-4">
            <div className="flex items-center gap-4">
              {forwardingRules.length < 10 && (
                <Button onClick={addRule} variant="outline">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {t("buildingSettings.questionsForwarding.addRule")}
                </Button>
              )}
              <Button
                onClick={() => handleSave(undefined, { showBanner: true })}
                disabled={!isAnyRuleDirty || isSaving || hasEmailErrors}
                loading={isSaving}
              >
                {isSaving ? t("settings.saving") : t("settings.save")}
              </Button>
            </div>
            <div className="flex-grow">
              {saveResult && (
                <ApiBanner
                  apiResponse={saveResult}
                  successMessage={t("buildingSettings.questionsForwarding.saveSuccess")}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
